import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import { useAppContext } from "@/contexts/AppContext";
import { supabase } from "@/lib/supabase";
import { recordInspectionOdometer } from "@/lib/vehicleOdometer";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";

type EditScope = "all" | "vehicle_only" | "odo_break";

type MergedShiftRow = {
  merged_id: string;
  driver_name: string;
  fleet: string | null;
  vehicle_number: string | null;
  shift_date: string; // YYYY-MM-DD
  start_time: string; // HH:mm:ss
  end_time: string | null; // HH:mm:ss
  segments_merged: number | null;
  source_shift_ids: string[] | null;
  suspicious_time: boolean | null;
  break_minutes_total: number | null;
};

type DbShiftRow = {
  id: string;
  driver_name: string;
  vehicle_number: string | null;
  shift_date: string; // YYYY-MM-DD
  start_time: string; // HH:mm:ss
  end_time: string | null; // HH:mm:ss
  start_odometer: number | null;
  end_odometer: number | null;
  break_time: number | null; // minutes
  total_km: number | null;
  shift_status: string | null; // active/completed
  fleet: string | null; // tg&b/stss
  updated_at: string | null;

  // override fields exist in table (needed for suspicious time override)
  override_start_time?: string | null; // HH:mm:ss
  override_end_time?: string | null; // HH:mm:ss
  override_reason?: string | null;
  override_by?: string | null;
  override_at?: string | null;
};

type EditableFields = {
  vehicle: boolean;
  startOdo: boolean;
  endOdo: boolean;
  breakTime: boolean;
};

const pad2 = (n: number) => String(n).padStart(2, "0");

const fmtTimeHHmm = (t?: string | null) => {
  if (!t) return "";
  return t.length >= 5 ? t.slice(0, 5) : t;
};

const fmtDateNZ = (isoDate?: string | null) => {
  if (!isoDate) return "";
  const parts = String(isoDate).split("-");
  if (parts.length !== 3) return String(isoDate);
  const [yyyy, mm, dd] = parts;
  return `${dd}/${mm}/${yyyy}`;
};

const normalizeFleetLabel = (fleet?: string | null) => {
  if (!fleet) return "Unknown";
  const f = String(fleet).toLowerCase();
  if (f === "tg&b") return "TG&B";
  if (f === "stss") return "STSS";
  return fleet;
};

const minutesBetween = (dateYYYYMMDD: string, startHHmmss: string, endHHmmss: string) => {
  const s = new Date(`${dateYYYYMMDD}T${startHHmmss}`);
  const e = new Date(`${dateYYYYMMDD}T${endHHmmss}`);
  let diff = Math.round((e.getTime() - s.getTime()) / 60000);
  if (diff < 0) diff += 24 * 60;
  return diff;
};

const fmtDurationHours = (minutes: number) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}:${pad2(m)}`;
};

const fmtBreakWeekly = (mins: number) => {
  if (!Number.isFinite(mins) || mins <= 0) return "0 min";
  if (mins < 60) return `${mins} min`;
  return `${fmtDurationHours(mins)} (${mins} min)`;
};

const isEditableWithin24h = (shift: { shift_date: string; end_time: string | null }) => {
  if (!shift.end_time || !shift.shift_date) return false;
  const end = new Date(`${shift.shift_date}T${shift.end_time}`);
  const now = new Date();
  const diffHours = (now.getTime() - end.getTime()) / (1000 * 60 * 60);
  return diffHours >= 0 && diffHours <= 24;
};

/** Editable for 1 week from shift_date if missing fields */
const isEditableWithin1Week = (shift: { shift_date: string }) => {
  const shiftDate = new Date(shift.shift_date + "T23:59:59");
  const now = new Date();
  const diffDays = (now.getTime() - shiftDate.getTime()) / (1000 * 60 * 60 * 24);
  return diffDays >= 0 && diffDays <= 7;
};

/** Check if a raw shift row has missing fields (end_time, vehicle, break_time, start_odo, end_odo) */
const rawShiftIsMissingFields = (raw: DbShiftRow): boolean => {
  if (!raw.end_time) return true;
  if (!raw.vehicle_number || raw.vehicle_number.trim() === "") return true;
  if (raw.break_time === null || raw.break_time === undefined) return true;
  if (raw.start_odometer === null || raw.start_odometer === undefined || raw.start_odometer === 0) return true;
  if (raw.end_odometer === null || raw.end_odometer === undefined) return true;
  return false;
};

/** Check if a merged shift has missing fields using merged data + raw shifts map */
const mergedShiftIsMissingFields = (
  merged: MergedShiftRow,
  rawMap: Map<string, DbShiftRow>
): boolean => {
  // Check merged-level fields
  if (!merged.end_time) return true;
  if (!merged.vehicle_number || merged.vehicle_number.trim() === "") return true;
  if (merged.break_minutes_total === null || merged.break_minutes_total === undefined) return true;

  // Check raw shift fields (odometers)
  const sourceIds = Array.isArray(merged.source_shift_ids) ? merged.source_shift_ids : [];
  for (const id of sourceIds) {
    const raw = rawMap.get(id);
    if (raw) {
      if (raw.start_odometer === null || raw.start_odometer === undefined || raw.start_odometer === 0) return true;
      if (raw.end_odometer === null || raw.end_odometer === undefined) return true;
    }
  }

  return false;
};


const numericFleet = (value: any) => {
  const s = String(value ?? "").trim();
  const n = parseInt(s, 10);
  return Number.isFinite(n) ? n : 999999;
};

const isHHmm = (v: string) => /^([01]\d|2[0-3]):([0-5]\d)$/.test(v.trim());
const toHHmmss = (hhmm: string) => `${hhmm.trim()}:00`;

interface ShiftFormProps {
  /**
   * Passed by DriverDashboard so this still resolves the right driver during
   * admin impersonation -- DriverViewModal.tsx hands driverName down as a
   * prop rather than touching AppContext's currentUser (which stays the
   * admin's own session throughout). Falls back to currentUser for a real
   * driver login, where this and AppContext agree anyway.
   */
  driverName?: string;
}

const ShiftForm: React.FC<ShiftFormProps> = ({ driverName: driverNameProp }) => {
  const { currentUser, fleetVehicles } = useAppContext();
  const driverName = driverNameProp || currentUser?.driver_name || "";

  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const [mergedShifts, setMergedShifts] = useState<MergedShiftRow[]>([]);
  const [rawShiftsMap, setRawShiftsMap] = useState<Map<string, DbShiftRow>>(new Map());
  const [weekOffset, setWeekOffset] = useState(0);


  const [editingMerged, setEditingMerged] = useState<MergedShiftRow | null>(null);
  const [currentEditingShift, setCurrentEditingShift] = useState<DbShiftRow | null>(null);

  const [editScope, setEditScope] = useState<EditScope>("all");

  // Local edit fields
  const [vehicleNumber, setVehicleNumber] = useState<string>("");
  const [startOdo, setStartOdo] = useState<string>("");
  const [endOdo, setEndOdo] = useState<string>("");
  const [breakTime, setBreakTime] = useState<string>("");

  // ✅ Suspicious time override UI fields
  const [overrideStartHHmm, setOverrideStartHHmm] = useState<string>("");
  const [overrideEndHHmm, setOverrideEndHHmm] = useState<string>("");
  const [overrideReason, setOverrideReason] = useState<string>("Suspicious time correction");

  const [saving, setSaving] = useState(false);

  // Set true when Get My Shift auto-opens the edit panel because the open
  // shift's start_odometer is still blank -- shows a prompt banner instead
  // of the driver having to notice it's missing themselves.
  const [autoPromptOdometer, setAutoPromptOdometer] = useState(false);

  const weekRange = useMemo(() => {
    const now = new Date();
    const d = new Date(now);
    d.setDate(d.getDate() + weekOffset * 7);

    const day = d.getDay(); // 0 Sun .. 6 Sat
    const diffToMonday = (day + 6) % 7;
    const monday = new Date(d);
    monday.setDate(d.getDate() - diffToMonday);

    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);

    const toISODate = (x: Date) =>
      `${x.getFullYear()}-${pad2(x.getMonth() + 1)}-${pad2(x.getDate())}`;

    const fromISO = toISODate(monday);
    const toISO = toISODate(sunday);

    return {
      from: fromISO,
      to: toISO,
      label: `${fmtDateNZ(fromISO)} → ${fmtDateNZ(toISO)}`,
    };
  }, [weekOffset]);

  const fetchMergedShifts = useCallback(async (): Promise<MergedShiftRow[]> => {
    if (!driverName) return [];
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("driver_shifts_merged_vw")
        .select(
          "merged_id,driver_name,fleet,vehicle_number,shift_date,start_time,end_time,segments_merged,source_shift_ids,suspicious_time,break_minutes_total"
        )
        .eq("driver_name", driverName)
        .gte("shift_date", weekRange.from)
        .lte("shift_date", weekRange.to)
        .order("shift_date", { ascending: false })
        .order("start_time", { ascending: false });

      if (error) {
        console.error("fetchMergedShifts error:", error);
        toast({ title: "Error", description: error.message || "Failed to load shifts." });
        return [];
      }

      const rows = Array.isArray(data) ? (data as MergedShiftRow[]) : [];
      setMergedShifts(rows);
      return rows;
    } finally {
      setLoading(false);
    }
  }, [driverName, weekRange.from, weekRange.to]);

  /** Fetch raw driver_shifts for the week to check odometer/break missing fields */
  const fetchRawShiftsForWeek = useCallback(async () => {
    if (!driverName) return;
    try {
      const { data, error } = await supabase
        .from("driver_shifts")
        .select("id,driver_name,vehicle_number,shift_date,start_time,end_time,start_odometer,end_odometer,break_time,total_km,shift_status,fleet,updated_at")
        .eq("driver_name", driverName)
        .gte("shift_date", weekRange.from)
        .lte("shift_date", weekRange.to);

      if (error) {
        console.error("fetchRawShiftsForWeek error:", error);
        return;
      }

      const map = new Map<string, DbShiftRow>();
      if (Array.isArray(data)) {
        for (const row of data as DbShiftRow[]) {
          map.set(row.id, row);
        }
      }
      setRawShiftsMap(map);
    } catch (e) {
      console.error("fetchRawShiftsForWeek unexpected error:", e);
    }
  }, [driverName, weekRange.from, weekRange.to]);

  useEffect(() => {
    fetchMergedShifts();
    fetchRawShiftsForWeek();
  }, [fetchMergedShifts, fetchRawShiftsForWeek]);


  const openMergedShift = useMemo(() => {
    const s = mergedShifts.find((x) => x.end_time === null);
    return s || null;
  }, [mergedShifts]);

  const pastMergedShifts = useMemo(() => {
    return mergedShifts
      .filter((s) => !!s.end_time)
      .sort((a, b) => {
        const aKey = `${a.shift_date}T${a.start_time}`;
        const bKey = `${b.shift_date}T${b.start_time}`;
        return aKey < bKey ? 1 : -1;
      });
  }, [mergedShifts]);

  const weeklyTotals = useMemo(() => {
    let totalOnlineMins = 0;
    let totalBreakMins = 0;

    for (const s of pastMergedShifts) {
      if (!s.end_time) continue;
      totalOnlineMins += minutesBetween(s.shift_date, s.start_time, s.end_time);
      totalBreakMins += Number.isFinite(s.break_minutes_total as any)
        ? (s.break_minutes_total as number)
        : 0;
    }

    const onDutyMins = Math.max(0, totalOnlineMins - totalBreakMins);

    return {
      totalOnlineLabel: fmtDurationHours(totalOnlineMins),
      totalBreaksLabel: fmtBreakWeekly(totalBreakMins),
      onDutyLabel: fmtDurationHours(onDutyMins),
    };
  }, [pastMergedShifts]);

  const vehicleOptions = useMemo(() => {
    const arr = Array.isArray(fleetVehicles) ? [...fleetVehicles] : [];
    arr.sort(
      (a: any, b: any) =>
        numericFleet(a.fleetNumber ?? a.vehicle_number ?? a.id) -
        numericFleet(b.fleetNumber ?? b.vehicle_number ?? b.id)
    );
    return arr;
  }, [fleetVehicles]);

  const editingIsPast = useMemo(() => !!currentEditingShift?.end_time, [currentEditingShift]);

  const canEditThisShift = useMemo(() => {
    if (!currentEditingShift || !editingMerged) return false;

    const suspicious = !!editingMerged.suspicious_time;

    // ✅ workaround: allow time override even if locked, but only for suspicious shifts
    if (suspicious) return true;

    if (!currentEditingShift.end_time) return true;
    if (isEditableWithin24h(currentEditingShift)) return true;

    // ✅ If shift has missing fields, allow editing for 1 week
    const hasMissing = rawShiftIsMissingFields(currentEditingShift);
    if (hasMissing && isEditableWithin1Week(currentEditingShift)) return true;

    return false;
  }, [currentEditingShift, editingMerged]);


  const editableFields: EditableFields = useMemo(() => {
    if (!editingMerged) return { vehicle: false, startOdo: false, endOdo: false, breakTime: false };

    const suspicious = !!editingMerged.suspicious_time;

    // If suspicious + locked (and NOT missing-fields-within-1-week), allow ONLY time override section
    const missingFieldsExtended = currentEditingShift
      ? rawShiftIsMissingFields(currentEditingShift) && isEditableWithin1Week(currentEditingShift)
      : false;

    if (suspicious && currentEditingShift?.end_time && !isEditableWithin24h(currentEditingShift) && !missingFieldsExtended) {
      return { vehicle: false, startOdo: false, endOdo: false, breakTime: false };
    }

    if (!editingIsPast) return { vehicle: true, startOdo: true, endOdo: true, breakTime: true };

    if (editScope === "vehicle_only") return { vehicle: true, startOdo: false, endOdo: false, breakTime: false };
    if (editScope === "odo_break") return { vehicle: false, startOdo: true, endOdo: true, breakTime: true };
    return { vehicle: true, startOdo: true, endOdo: true, breakTime: true };
  }, [editingIsPast, editScope, editingMerged, currentEditingShift]);


  const loadRawShiftForMergedRow = useCallback(async (m: MergedShiftRow): Promise<DbShiftRow | null> => {
    const sourceIds = Array.isArray(m.source_shift_ids) ? m.source_shift_ids : [];
    const rawId = sourceIds[0];

    if (!rawId) {
      toast({ title: "No source shift", description: "This merged shift has no source IDs to edit." });
      return null;
    }

    const { data, error } = await supabase
      .from("driver_shifts")
      .select(
        "id,driver_name,vehicle_number,shift_date,start_time,end_time,start_odometer,end_odometer,break_time,total_km,shift_status,fleet,updated_at,override_start_time,override_end_time,override_reason,override_by,override_at"
      )
      .eq("id", rawId)
      .maybeSingle();

    if (error) {
      console.error("loadRawShiftForMergedRow error:", error);
      toast({ title: "Error", description: error.message || "Failed to load shift for editing." });
      return null;
    }

    const row = (data as DbShiftRow | null) ?? null;
    if (!row) {
      toast({ title: "Not found", description: "Could not find the source shift row." });
      return null;
    }

    setCurrentEditingShift(row);

    setVehicleNumber(row.vehicle_number ?? "");
    setStartOdo(row.start_odometer != null ? String(row.start_odometer) : "");
    setEndOdo(row.end_odometer != null ? String(row.end_odometer) : "");
    setBreakTime(row.break_time != null ? String(row.break_time) : "");

    // Default override inputs to the merged displayed times (cleaner UX)
    setOverrideStartHHmm(fmtTimeHHmm(m.start_time));
    setOverrideEndHHmm(fmtTimeHHmm(m.end_time));
    setOverrideReason("Suspicious time correction");

    return row;
  }, []);

  const handleNumberInput = (value: string, setter: (v: string) => void) => {
    setter(value.replace(/[^0-9]/g, ""));
  };

  const numericStartOdo = useMemo(() => {
    const n = parseInt(startOdo || "0", 10);
    return Number.isFinite(n) ? n : 0;
  }, [startOdo]);

  const numericEndOdo = useMemo(() => {
    if (!endOdo) return undefined;
    const n = parseInt(endOdo, 10);
    return Number.isFinite(n) ? n : undefined;
  }, [endOdo]);

  const numericBreak = useMemo(() => {
    if (!breakTime) return null;
    const n = parseInt(breakTime, 10);
    return Number.isFinite(n) ? n : null;
  }, [breakTime]);

  const computedKm = useMemo(() => {
    if (typeof numericEndOdo !== "number") return null;
    const km = numericEndOdo - numericStartOdo;
    return km > 0 ? km : 0;
  }, [numericStartOdo, numericEndOdo]);

  const handleGetMyShift = async () => {
    setSyncing(true);
    try {
      const { data, error } = await supabase.functions.invoke("shift-manager", {
        body: { force: true, dryRun: false },
      });

      if (error) {
        console.error("shift-manager invoke error:", error);
        toast({ title: "Shift sync failed", description: error.message || "Could not sync shifts." });
        return;
      }

      const msg =
        data?.metrics
          ? `Pulled: ${data.metrics?.start?.upsert_attempted ?? 0} starts, updated: ${data.metrics?.end?.updated ?? 0} ends`
          : "Shift sync completed.";

      toast({ title: "Shifts updated", description: msg });

      const freshMerged = await fetchMergedShifts();
      await fetchRawShiftsForWeek();

      // If the open shift still has no starting odometer, open the edit
      // panel on it automatically instead of relying on the driver to
      // notice and click Edit themselves -- this is how "record the KM
      // for the vehicle you're in" actually gets recorded.
      const openShift = freshMerged.find((s) => s.end_time === null) || null;
      if (openShift) {
        const raw = await loadRawShiftForMergedRow(openShift);
        if (raw && !raw.start_odometer) {
          setEditingMerged(openShift);
          setEditScope("all");
          setAutoPromptOdometer(true);
        }
      }
    } catch (e: any) {
      console.error("handleGetMyShift error:", e);
      toast({ title: "Shift sync failed", description: "Unexpected error calling shift-manager." });
    } finally {
      setSyncing(false);
    }
  };

  const validateSave = () => {
    if (!currentEditingShift?.id || !editingMerged) {
      toast({ title: "No shift selected", description: "Please choose a shift to edit." });
      return false;
    }

    // If suspicious, validate time override inputs (because that’s the point)
    if (editingMerged.suspicious_time) {
      if (!isHHmm(overrideStartHHmm) || !isHHmm(overrideEndHHmm)) {
        toast({
          title: "Invalid time",
          description: "Override times must be in HH:mm (e.g. 08:00).",
        });
        return false;
      }
    }

    // Normal validations for non-locked edit fields
    if (editableFields.vehicle && !vehicleNumber) {
      toast({ title: "Missing vehicle", description: "Please select a vehicle." });
      return false;
    }

    if (editableFields.startOdo && !startOdo) {
      toast({ title: "Missing odometer", description: "Please enter your start odometer." });
      return false;
    }

    if (editableFields.endOdo && endOdo && typeof numericEndOdo === "number") {
      if (numericEndOdo <= numericStartOdo) {
        toast({
          title: "Odometer mismatch",
          description: "End odometer must be greater than start odometer.",
        });
        return false;
      }
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateSave() || !editingMerged) return;

    setSaving(true);
    try {
      const nowIso = new Date().toISOString();
      const sourceIds = Array.isArray(editingMerged.source_shift_ids) ? editingMerged.source_shift_ids : [];
      const firstId = sourceIds[0];
      const lastId = sourceIds[sourceIds.length - 1];

      // 1) Save normal editable fields (single row update – the anchor row)
      if (currentEditingShift?.id) {
        const payload: Record<string, any> = { updated_at: nowIso };

        if (editableFields.vehicle) payload.vehicle_number = vehicleNumber || null;
        if (editableFields.startOdo) payload.start_odometer = numericStartOdo;
        if (editableFields.endOdo) payload.end_odometer = typeof numericEndOdo === "number" ? numericEndOdo : null;
        if (editableFields.breakTime) payload.break_time = numericBreak;

        if (typeof numericEndOdo === "number") {
          payload.total_km = computedKm !== null ? Math.round(computedKm) : null;
        }

        // only do this update if there are actual fields to update besides updated_at
        const keys = Object.keys(payload).filter((k) => k !== "updated_at");
        if (keys.length) {
          const { error } = await supabase.from("driver_shifts").update(payload).eq("id", currentEditingShift.id);
          if (error) {
            console.error("Error saving shift fields:", error);
            toast({ title: "Error", description: error.message || "Failed to save shift details." });
            return;
          }
        }

        // Wire whatever odometer reading(s) were entered into the vehicle's
        // "last KM" (only if higher than what's on file -- see
        // recordInspectionOdometer) so RUC/service/tyre-rotation tracking
        // actually gets fed from shift odometer entries, not just vehicle
        // checks. Called for both start and end when present -- safe to
        // call twice since each call re-checks against the current value.
        const odoVehicle = vehicleNumber || currentEditingShift.vehicle_number;
        if (odoVehicle) {
          if (editableFields.startOdo && numericStartOdo) {
            await recordInspectionOdometer(odoVehicle, numericStartOdo);
          }
          if (editableFields.endOdo && typeof numericEndOdo === "number") {
            await recordInspectionOdometer(odoVehicle, numericEndOdo);
          }
        }
      }

      // 2) ✅ Suspicious time override — update first and last segment times
      if (editingMerged.suspicious_time) {
        if (!firstId || !lastId) {
          toast({ title: "Missing segments", description: "Could not find source segment IDs to override." });
          return;
        }

        const common = {
          override_reason: overrideReason || "Suspicious time correction",
          override_by: driverName || "unknown",
          override_at: nowIso,
          updated_at: nowIso,
        };

        const { error: errStart } = await supabase
          .from("driver_shifts")
          .update({ ...common, override_start_time: toHHmmss(overrideStartHHmm) })
          .eq("id", firstId);

        if (errStart) {
          console.error("override_start_time update error:", errStart);
          toast({ title: "Error", description: errStart.message || "Failed to save override start time." });
          return;
        }

        const { error: errEnd } = await supabase
          .from("driver_shifts")
          .update({ ...common, override_end_time: toHHmmss(overrideEndHHmm) })
          .eq("id", lastId);

        if (errEnd) {
          console.error("override_end_time update error:", errEnd);
          toast({ title: "Error", description: errEnd.message || "Failed to save override end time." });
          return;
        }
      }

      toast({ title: "Saved", description: "Shift details updated." });

      setEditingMerged(null);
      setCurrentEditingShift(null);
      setAutoPromptOdometer(false);

      await fetchMergedShifts();
    } catch (err) {
      console.error("Unexpected save error:", err);
      toast({ title: "Error", description: "Failed to save shift details." });
    } finally {
      setSaving(false);
    }
  };

  const getMissingWarnings = (s: MergedShiftRow) => {
    const missing: string[] = [];

    const vehicleMissing = !s.vehicle_number || String(s.vehicle_number).trim() === "";
    if (vehicleMissing) missing.push("Vehicle");

    // Break required if > 7 hours
    if (s.end_time) {
      const shiftMins = minutesBetween(s.shift_date, s.start_time, s.end_time);
      const breakMins = Number.isFinite(s.break_minutes_total as any) ? (s.break_minutes_total as number) : 0;
      if (shiftMins >= 7 * 60 && breakMins <= 0) missing.push("Break Time");
    }

    // Odometer readings were already tracked internally (rawShiftIsMissingFields
    // widens the edit window for them) but never actually surfaced to the
    // driver as "missing" -- add them here so a blank reading is visible,
    // not just silently absent.
    const sourceIds = Array.isArray(s.source_shift_ids) ? s.source_shift_ids : [];
    const firstRaw = sourceIds.length ? rawShiftsMap.get(sourceIds[0]) : undefined;
    const lastRaw = sourceIds.length ? rawShiftsMap.get(sourceIds[sourceIds.length - 1]) : undefined;
    if (firstRaw && (firstRaw.start_odometer === null || firstRaw.start_odometer === undefined || firstRaw.start_odometer === 0)) {
      missing.push("Start Odometer");
    }
    if (s.end_time && lastRaw && (lastRaw.end_odometer === null || lastRaw.end_odometer === undefined)) {
      missing.push("End Odometer");
    }

    return missing;
  };

  const renderMergedShiftSummary = (s: MergedShiftRow) => {
    const start = fmtTimeHHmm(s.start_time);
    const end = fmtTimeHHmm(s.end_time);
    const fleetLabel = normalizeFleetLabel(s.fleet);
    const vehicle = s.vehicle_number ? s.vehicle_number : "(not set)";
    const dateNZ = fmtDateNZ(s.shift_date);
    const segs = typeof s.segments_merged === "number" ? s.segments_merged : 1;
    const suspicious = !!s.suspicious_time;

    if (!s.end_time) {
      return (
        <>
          <div className="font-semibold">
            {dateNZ} — {start} → <span className="text-yellow-300">Open</span>
            {suspicious ? <span className="ml-2 text-red-600">⚠ suspicious time</span> : null}
          </div>
          <div className="text-sm text-muted-foreground">
            Fleet: {fleetLabel} • Vehicle: {vehicle}
            {segs > 1 ? <> • Segments: {segs}</> : null}
          </div>
          {getMissingWarnings(s).length ? (
            <div className="text-sm font-semibold text-red-500 mt-1">
              Missing: {getMissingWarnings(s).join(", ")}
            </div>
          ) : null}
        </>
      );
    }

    const mins = minutesBetween(s.shift_date, s.start_time, s.end_time);
    const hours = fmtDurationHours(mins);
    const breakMins = Number.isFinite(s.break_minutes_total as any) ? (s.break_minutes_total as number) : 0;
    const breakLabel = fmtBreakWeekly(breakMins);

    const missing = getMissingWarnings(s);

    return (
      <>
        <div className="font-semibold">
          {dateNZ} — {start} to {end}
          {suspicious ? <span className="ml-2 text-red-600">⚠ suspicious time</span> : null}
        </div>
        <div className="text-sm text-muted-foreground">
          Total: {hours} • Breaks: {breakLabel} • Fleet: {fleetLabel} • Vehicle: {vehicle}
          {segs > 1 ? <> • Segments: {segs}</> : null}
        </div>

        {missing.length ? (
          <div className="text-sm font-semibold text-red-500 mt-1">
            Missing: {missing.join(", ")}
          </div>
        ) : null}
      </>
    );
  };

  if (!currentUser) {
    return (
      <Card className="shadow-lg">
        <CardHeader>
          <CardTitle className="text-xl">Shift Management</CardTitle>
        </CardHeader>
        <CardContent>Please log in to view your shifts.</CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="shadow-lg">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-xl">Shift Management</CardTitle>
            <div className="text-sm text-muted-foreground">Driver: {driverName}</div>
          </div>

          <div className="flex gap-2 items-center">
            <Button variant="outline" onClick={() => setWeekOffset((w) => w - 1)} disabled={loading || syncing}>
              ◀
            </Button>
            <div className="text-sm text-muted-foreground">{weekRange.label}</div>
            <Button variant="outline" onClick={() => setWeekOffset((w) => w + 1)} disabled={loading || syncing}>
              ▶
            </Button>

            <Button onClick={handleGetMyShift} disabled={syncing}>
              {syncing ? "Updating..." : "Get My Shift"}
            </Button>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Current / Open Shift */}
          <div className="space-y-2">
            <div className="font-semibold">Current / Open Shift</div>

            {openMergedShift ? (
              <div className="rounded-lg border p-3 flex items-center justify-between">
                <div>{renderMergedShiftSummary(openMergedShift)}</div>

                <Button
                  variant="outline"
                  onClick={async () => {
                    setEditingMerged(openMergedShift);
                    setEditScope("all");
                    setAutoPromptOdometer(false);
                    await loadRawShiftForMergedRow(openMergedShift);
                  }}
                >
                  Edit
                </Button>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">No open shift found for this week.</div>
            )}
          </div>

          {/* Edit panel */}
          {editingMerged && currentEditingShift && (
            <div className="pt-4 border-t space-y-4">
              {autoPromptOdometer && (
                <div className="rounded-lg border border-amber-400 bg-amber-50 text-amber-900 px-3 py-2 text-sm font-medium">
                  Please enter your starting odometer reading below for the vehicle you're in.
                </div>
              )}
              <div className="flex items-center justify-between">
                <div className="font-semibold">Edit Shift</div>
                <Button
                  variant="outline"
                  onClick={() => {
                    setAutoPromptOdometer(false);
                    setEditingMerged(null);
                    setCurrentEditingShift(null);
                  }}
                >
                  Close
                </Button>
              </div>

              {/* Scope selector for past shifts (only if NOT locked-but-suspicious-only mode) */}
              {editingIsPast && !(editingMerged.suspicious_time && currentEditingShift.end_time && !isEditableWithin24h(currentEditingShift)) && (
                <div className="max-w-sm">
                  <Label>What do you need to update?</Label>
                  <Select value={editScope} onValueChange={(v) => setEditScope(v as EditScope)}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Choose..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Vehicle + Odometer + Breaks</SelectItem>
                      <SelectItem value="vehicle_only">Vehicle only</SelectItem>
                      <SelectItem value="odo_break">Odometer + Breaks</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {!canEditThisShift ? (
                <div className="text-sm text-muted-foreground">
                  This shift is locked (past the 24h edit window).
                </div>
              ) : (
                <Card className="shadow-lg">
                  <CardHeader>
                    <CardTitle className="text-lg text-blue-800">Edit Shift</CardTitle>
                  </CardHeader>

                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div>
                        <Label>Shift Date (from TaxiCaller)</Label>
                        <Input value={fmtDateNZ(currentEditingShift.shift_date) || ""} disabled />
                      </div>
                      <div>
                        <Label>Start Time (from TaxiCaller)</Label>
                        <Input value={fmtTimeHHmm(editingMerged.start_time)} disabled />
                      </div>
                      <div>
                        <Label>End Time (from TaxiCaller)</Label>
                        <Input value={fmtTimeHHmm(editingMerged.end_time)} disabled />
                      </div>
                    </div>

                    {/* ✅ Suspicious time override section */}
                    {editingMerged.suspicious_time ? (
                      <div className="rounded-lg border p-3 space-y-3">
                        <div className="font-semibold text-red-600">⚠ Suspicious time override</div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          <div>
                            <Label>Override Start (HH:mm)</Label>
                            <Input
                              value={overrideStartHHmm}
                              onChange={(e) => setOverrideStartHHmm(e.target.value)}
                              placeholder="08:00"
                            />
                          </div>

                          <div>
                            <Label>Override End (HH:mm)</Label>
                            <Input
                              value={overrideEndHHmm}
                              onChange={(e) => setOverrideEndHHmm(e.target.value)}
                              placeholder="18:17"
                            />
                          </div>

                          <div>
                            <Label>Reason</Label>
                            <Input
                              value={overrideReason}
                              onChange={(e) => setOverrideReason(e.target.value)}
                              placeholder="Suspicious time correction"
                            />
                          </div>
                        </div>

                        <div className="text-xs text-muted-foreground">
                          This will update the start time on the first segment and the end time on the last segment for this merged block.
                        </div>
                      </div>
                    ) : null}

                    {/* Normal editable fields */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label>Select Vehicle</Label>
                        <Select
                          value={vehicleNumber}
                          onValueChange={setVehicleNumber}
                          disabled={!editableFields.vehicle}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select a vehicle" />
                          </SelectTrigger>
                          <SelectContent>
                            {vehicleOptions.map((v: any) => {
                              const value = v.fleetNumber ?? v.vehicle_number ?? v.id;
                              const label =
                                v.fleetNumber && v.registration
                                  ? `${v.fleetNumber} - ${v.registration}`
                                  : String(value);
                              return (
                                <SelectItem key={String(value)} value={String(value)}>
                                  {label}
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label>Break Time (minutes)</Label>
                        <Input
                          value={breakTime}
                          onChange={(e) => handleNumberInput(e.target.value, setBreakTime)}
                          placeholder="e.g. 30"
                          disabled={!editableFields.breakTime}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label>Start Odometer</Label>
                        <Input
                          value={startOdo}
                          onChange={(e) => handleNumberInput(e.target.value, setStartOdo)}
                          placeholder="e.g. 123456"
                          disabled={!editableFields.startOdo}
                        />
                      </div>

                      <div>
                        <Label>End Odometer</Label>
                        <Input
                          value={endOdo}
                          onChange={(e) => handleNumberInput(e.target.value, setEndOdo)}
                          placeholder="e.g. 123789"
                          disabled={!editableFields.endOdo}
                        />
                      </div>
                    </div>

                    {computedKm !== null && (
                      <div className="text-sm font-semibold text-blue-700">
                        Estimated KM (from odometer): {computedKm}
                      </div>
                    )}

                    <div className="flex gap-2">
                      <Button onClick={handleSave} disabled={saving}>
                        {saving ? "Saving..." : "Save changes"}
                      </Button>
                    </div>

                    <div className="text-xs text-muted-foreground">
                      Start/end times come from TaxiCaller. If your end time is missing, log out in TaxiCaller and tap{" "}
                      <strong>Get My Shift</strong> again.
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Past shifts */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="font-semibold">Past Shifts</div>

              <div className="text-sm text-muted-foreground">
                Total Online: <span className="font-semibold">{weeklyTotals.totalOnlineLabel}</span>{" "}
                • Total Breaks: <span className="font-semibold">{weeklyTotals.totalBreaksLabel}</span>{" "}
                • Total On Duty: <span className="font-semibold">{weeklyTotals.onDutyLabel}</span>
              </div>
            </div>

            {pastMergedShifts.length ? (
              <div className="space-y-2">
                {pastMergedShifts.map((s) => {
                  const within24h = isEditableWithin24h(s);
                  const suspicious = !!s.suspicious_time;
                  const hasMissing = mergedShiftIsMissingFields(s, rawShiftsMap);
                  const within1Week = isEditableWithin1Week(s);
                  const missingFieldsEditable = hasMissing && within1Week;
                  const editable = within24h || suspicious || missingFieldsEditable;

                  // Determine label
                  let editLabel = "Locked";
                  if (suspicious) editLabel = "Editable (suspicious time)";
                  else if (within24h) editLabel = "Editable (24h)";
                  else if (missingFieldsEditable) editLabel = "Editable (1 week - missing fields)";

                  return (
                    <div key={s.merged_id} className="rounded-lg border p-3 flex items-center justify-between">
                      <div>
                        {renderMergedShiftSummary(s)}
                        <div className="text-xs text-muted-foreground mt-1">
                          {editLabel}
                        </div>
                      </div>


                      <Button
                        variant="outline"
                        disabled={!editable}
                        onClick={async () => {
                          setEditingMerged(s);
                          setEditScope("all");
                          setAutoPromptOdometer(false);
                          await loadRawShiftForMergedRow(s);
                        }}
                      >
                        Edit
                      </Button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">No past shifts in this week.</div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ShiftForm;
